/**
 * win-celebration.tsx — NL-034 Daily WIN celebration visual.
 *
 * Once-per-day-per-client flash banner that surfaces when the client
 * has saved today's Daily WIN (NL-033). Renders an animated brand-
 * styled banner with a flame burst + the WIN text, auto-dismisses
 * after 4 seconds.
 *
 * Mount on:
 *  - /check-in — after the client saves the WIN for the first time
 *    today, the celebration fades in below the date nav.
 *  - /dashboard — when the client opens the dashboard AND the WIN was
 *    already saved today AND the celebration hasn't fired yet today.
 *
 * Once-per-day guard uses localStorage with the key
 * `nl034_win_celebration_<clientId>_<YYYY-MM-DD>`. Cleared on a new
 * local day so the next day's first WIN celebrates again.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  /** Owning client id — keys the localStorage guard so multi-client coaches don't trip each other. */
  clientId: string;
  /** ISO local-day (YYYY-MM-DD) the WIN belongs to. */
  date: string;
  /** Saved WIN text. Empty / null / whitespace = nothing to celebrate (component returns null). */
  winText: string | null | undefined;
  /**
   * When true, mount the banner even if the once-per-day guard says
   * "already fired today". Used by /check-in to flash the celebration
   * immediately on the save action (the user wants to see it land).
   * Default false (Dashboard reads): suppressed after the first show.
   */
  forceShow?: boolean;
}

const AUTO_DISMISS_MS = 4000;

function storageKey(clientId: string, date: string): string {
  return `nl034_win_celebration_${clientId}_${date}`;
}

/** Exported for tests; resets the once-per-day guard for a given client + date. */
export function clearWinCelebrationGuard(clientId: string, date: string): void {
  try {
    window.localStorage.removeItem(storageKey(clientId, date));
  } catch {
    /* SSR / privacy mode — silently drop. */
  }
}

export function WinCelebration({
  clientId,
  date,
  winText,
  forceShow = false,
}: Props): JSX.Element | null {
  const { t } = useTranslation();
  const trimmed = (winText ?? '').trim();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (trimmed.length === 0) {
      setVisible(false);
      return;
    }
    let alreadyFired = false;
    try {
      alreadyFired = window.localStorage.getItem(storageKey(clientId, date)) === '1';
    } catch {
      /* SSR / privacy mode — treat as not-fired. */
    }
    if (!forceShow && alreadyFired) {
      setVisible(false);
      return;
    }
    setVisible(true);
    try {
      window.localStorage.setItem(storageKey(clientId, date), '1');
    } catch {
      /* ignore */
    }
    const timer = window.setTimeout(() => setVisible(false), AUTO_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [trimmed, clientId, date, forceShow]);

  if (!visible || trimmed.length === 0) return null;

  return (
    <div
      data-testid="win-celebration"
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 top-s5 z-50 flex justify-center pointer-events-none"
    >
      <div
        className="
          relative max-w-md mx-s4
          rounded-2xl border border-flame/40 bg-bone shadow-xl
          px-s5 py-s4 pointer-events-auto
          animate-[winPop_0.55s_ease-out]
        "
      >
        {/* Flame burst — pure CSS, brand colours. Positioned behind the text. */}
        <span
          aria-hidden="true"
          className="
            absolute -top-s4 left-1/2 -translate-x-1/2
            text-[40px] leading-none select-none
            animate-[winFlame_0.9s_ease-out]
          "
        >
          🔥
        </span>
        <div className="text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-flame-ember mb-s1">
            {t('checkIn.dailyWin.celebration.eyebrow')}
          </p>
          <h3 className="font-display text-display-sm text-ink leading-tight">
            {t('checkIn.dailyWin.celebration.headline')}
          </h3>
          <p className="mt-s2 font-display text-base text-ink italic line-clamp-3">
            &ldquo;{trimmed}&rdquo;
          </p>
          <p className="mt-s3 font-mono text-[10px] text-ink/50">
            {t('checkIn.dailyWin.celebration.tagline')}
          </p>
        </div>
      </div>
      {/* Inline keyframes — brand uses Tailwind tokens but the bespoke
          animation isn't worth a tailwind.config plugin for a single use. */}
      <style>{`
        @keyframes winPop {
          0%   { transform: translateY(-12px) scale(0.92); opacity: 0; }
          60%  { transform: translateY(0) scale(1.04); opacity: 1; }
          100% { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes winFlame {
          0%   { transform: translate(-50%, 8px) scale(0.4); opacity: 0; }
          40%  { transform: translate(-50%, -2px) scale(1.2); opacity: 1; }
          100% { transform: translate(-50%, 0) scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
